import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Award, Crown, Flame, Heart, PartyPopper, Trophy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useActivityFeed, useBoard } from '../lib/data';
import { reactToActivity } from '../lib/api';
import { istMonthKey } from '../lib/time';
import type { ActivityItem, BoardUser } from '../types';
import SiteHeader from './SiteHeader';

export default function Dangal() {
  const { user } = useAuth();
  const [scope, setScope] = useState<'month' | 'allTime'>('month');
  const monthKey = istMonthKey();
  const board = useBoard(monthKey, scope);
  const feed = useActivityFeed();

  return (
    <div className="min-h-screen bg-paper-50 dark:bg-clay-floor">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 pb-16 pt-4">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-ink-400 hover:text-terracotta-600">
          <ArrowLeft className="h-4 w-4" /> Home
        </Link>

        <div className="mt-3 flex items-center gap-2">
          <Trophy className="h-5 w-5 text-terracotta-500" />
          <h1 className="text-xl font-semibold text-ink-900 dark:text-paper-50">The Dangal</h1>
        </div>
        <p className="mt-0.5 text-sm text-ink-500 dark:text-paper-300">
          Cheering each other on — never keeping score against you.
        </p>

        {/* Scope toggle */}
        <div className="mt-4 inline-flex rounded-lg border border-rule bg-paper-0 p-0.5 text-sm dark:border-clay-rule dark:bg-clay-surface">
          {(['month', 'allTime'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`rounded-md px-3 py-1 font-medium transition ${
                scope === s
                  ? 'bg-terracotta-500 text-white'
                  : 'text-ink-500 hover:text-ink-900 dark:text-paper-300'
              }`}
            >
              {s === 'month' ? 'This month' : 'All time'}
            </button>
          ))}
        </div>

        <Leaderboard rows={board} scope={scope} meUid={user?.uid} />

        <section className="mt-8">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            <PartyPopper className="h-3.5 w-3.5 text-terracotta-500" /> Wins on the mat
          </div>
          <Feed items={feed} meUid={user?.uid} />
        </section>
      </main>
    </div>
  );
}

function Leaderboard({
  rows,
  scope,
  meUid,
}: {
  rows: BoardUser[];
  scope: 'month' | 'allTime';
  meUid: string | undefined;
}) {
  if (rows.length === 0) {
    return (
      <div className="mt-4 rounded-xl border border-dashed border-rule p-8 text-center text-sm text-ink-400 bg-paper-100/50 dark:border-clay-rule dark:bg-clay-surface/60">
        No Zor on the board yet this {scope === 'month' ? 'month' : 'season'} — be the first to step up.
      </div>
    );
  }
  return (
    <ol className="mt-4 flex flex-col gap-1.5">
      {rows.map((r, i) => {
        const me = r.uid === meUid;
        const reps = scope === 'month' ? r.points.month : r.points.allTime;
        return (
          <li
            key={r.uid}
            className={`flex items-center gap-3 rounded-xl border p-3 ${
              me
                ? 'border-terracotta-300 bg-terracotta-50 dark:border-terracotta-700 dark:bg-terracotta-900/20'
                : 'border-rule bg-paper-0 dark:border-clay-rule dark:bg-clay-surface'
            }`}
          >
            <span className="flex w-6 shrink-0 justify-center">
              {i === 0 ? (
                <Crown className="h-5 w-5 text-terracotta-500" aria-label="Rustam" />
              ) : (
                <span className="text-sm font-semibold text-ink-400">{i + 1}</span>
              )}
            </span>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-verdant-700 text-[11px] font-semibold text-white">
              {r.initials}
            </span>
            <span className="min-w-0 flex-1 truncate font-medium text-ink-900 dark:text-paper-50">
              {r.displayName}
              {me && <span className="ml-1 text-xs text-terracotta-600">· you</span>}
              {i === 0 && (
                <span className="ml-1 text-[11px] uppercase tracking-wide text-terracotta-500">
                  Rustam
                </span>
              )}
            </span>
            <span className="flex shrink-0 items-center gap-1 font-semibold text-terracotta-600 dark:text-terracotta-300">
              {reps}
              <span className="text-xs font-normal text-ink-400">Zor</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Feed({ items, meUid }: { items: ActivityItem[]; meUid: string | undefined }) {
  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-rule p-8 text-center text-sm text-ink-400 bg-paper-100/50 dark:border-clay-rule dark:bg-clay-surface/60">
        Quiet on the mat. Complete a paintra and you’ll be the first win here.
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {items.map((it) => (
        <FeedRow key={it.id} item={it} meUid={meUid} />
      ))}
    </ul>
  );
}

function FeedRow({ item, meUid }: { item: ActivityItem; meUid: string | undefined }) {
  const alreadyReacted = !!meUid && Array.isArray(item.reactedBy) && item.reactedBy.includes(meUid);
  const [busy, setBusy] = useState(false);
  const Icon = item.type === 'track_complete' ? Award : item.type === 'badge' ? Award : Flame;

  const onReact = async () => {
    if (busy || alreadyReacted) return;
    setBusy(true);
    await reactToActivity(item.id); // feed updates via snapshot
    setBusy(false);
  };

  return (
    <li className="flex items-center gap-3 rounded-xl border border-rule bg-paper-0 p-3 dark:border-clay-rule dark:bg-clay-surface">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-verdant-700 text-[11px] font-semibold text-white">
        {item.initials}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ink-900 dark:text-paper-50">
          <span className="font-semibold">{item.userName}</span>{' '}
          <span className="text-ink-500 dark:text-paper-300">{item.label}</span>
        </p>
        <p className="flex items-center gap-1 text-xs text-ink-400">
          <Icon className="h-3 w-3 text-terracotta-400" />
          <span className="capitalize">{item.type.replace('_', ' ')}</span>
        </p>
      </div>
      <button
        onClick={() => void onReact()}
        disabled={busy || alreadyReacted}
        title={alreadyReacted ? 'You cheered this' : 'Cheer on'}
        className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-medium transition ${
          alreadyReacted
            ? 'bg-terracotta-500 text-white'
            : 'bg-paper-100 text-ink-500 hover:bg-terracotta-50 hover:text-terracotta-600 dark:bg-clay-raised dark:text-paper-300'
        } disabled:cursor-default`}
      >
        <Heart className={`h-3.5 w-3.5 ${alreadyReacted ? 'fill-current' : ''}`} />
        {item.reactions?.heart ?? 0}
      </button>
    </li>
  );
}
